import { forwardRef, type InputHTMLAttributes, type ReactNode } from "react";

import { cn } from "@/lib/cn";

export interface InputProps extends InputHTMLAttributes<HTMLInputElement> {
  /**
   * When true, the input border turns `--negative`. Message copy lives in the
   * calling form component (e.g., a FormField wrapper) so <Input> stays
   * presentational and can be reused in non-form contexts.
   */
  error?: boolean;
  /**
   * Optional node rendered inside the input's right padding — typically a
   * small icon button (eye toggle, clear button, unit label). The wrapper is
   * `position: relative` only when this slot is populated so the
   * non-decorated input keeps its original DOM surface.
   */
  trailingIcon?: ReactNode;
  /**
   * Optional decorative node (typically a 14px search icon) rendered inside
   * the input's left padding. Non-interactive; pointer events pass through.
   */
  leadingIcon?: ReactNode;
}

export const Input = forwardRef<HTMLInputElement, InputProps>(function Input(
  { className, error, type = "text", trailingIcon, leadingIcon, ...props },
  ref,
) {
  const input = (
    <input
      ref={ref}
      type={type}
      aria-invalid={error || undefined}
      className={cn(
        // v2 input: 36px paper field with indigo focus halo (3px rgba glow).
        "h-9 w-full rounded-[var(--r-md)] border bg-[var(--surface)] px-3 text-[13px] text-[var(--ink)] font-sans",
        "placeholder:text-[var(--ink-4)]",
        "transition-[border-color,box-shadow] duration-fast ease",
        "focus-visible:outline-none focus-visible:border-[var(--indigo)] focus-visible:shadow-[0_0_0_3px_rgba(79,70,229,0.15)]",
        "disabled:opacity-50 disabled:cursor-not-allowed",
        error ? "border-[var(--neg)]" : "border-[var(--line-2)]",
        // Reserve room on the right for the trailing icon so its absolute
        // position never overlaps typed content.
        trailingIcon && "pr-10",
        leadingIcon && "pl-[30px]",
        className,
      )}
      {...props}
    />
  );

  if (!trailingIcon && !leadingIcon) {
    return input;
  }

  return (
    <div className="relative">
      {input}
      {leadingIcon && (
        <span
          aria-hidden
          className="pointer-events-none absolute inset-y-0 left-2.5 flex items-center text-[var(--ink-4)]"
        >
          {leadingIcon}
        </span>
      )}
      {/*
        Absolute overlay sits inside the input's right padding. Height matches
        the field so caller-provided 40×40 buttons stay centered and remain
        clickable without stealing focus from the text surface.
      */}
      {trailingIcon && (
        <div className="pointer-events-none absolute inset-y-0 right-0 flex items-center pr-1">
          <div className="pointer-events-auto flex items-center">
            {trailingIcon}
          </div>
        </div>
      )}
    </div>
  );
});
